import React, { useState, useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  TableCell,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  TableBody,
  Typography,
  Box,
  Grid,
  Button,
  Link,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import history from "../services/history";
import { Trans, useTranslation } from "react-i18next";
import WBPPage from "./sub-components/WBPPage";
import BeeCareOrderSideBar from "./BeeCareOrderSideBar";
import UserContext from "../contexts/UserContext";
import BeeCareOrderContext from "../contexts/BeeCareOrderContext";
import SettingsContext from "../contexts/SettingsContext";
import { useFormatter } from "../hooks/formatter.hooks";
import { useMount } from "react-use";
import { StoreOutlined, InfoOutlined } from "@mui/icons-material";
import BeehomeModal from "./modals/BeehomeModal";
import references from "../services/references";
import { fetchBeeHomeAddOnProducts } from "../services/shopifyService";
import { calculateCompleteOrderTotal } from "../services/beecareOrderService";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  checkboxActive: {
    color: `${theme.palette.primary.main} !important`,
  },
  checkHolder: {
    minWidth: 0,
  },
  houseImage: {
    maxWidth: 90,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      maxWidth: 60,
      marginRight: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  },
  houseRowActive: {
    cursor: "pointer",
  },
  houseRowInActive: {
    opacity: ".5",
  },
  imgSelected: {
    border: `medium solid ${theme.palette.primary.main}`,
    borderRadius: "3px",
  },
  listTitle: {
    "& .MuiListItemText-primary": {
      color: "black",
      fontWeight: "bold",
    },
    "& .MuiListItemText-secondary": {
      fontWeight: "bold",
    },
  },
  mobileMargin: {
    [theme.breakpoints.down("md")]: {
      margin: 0,
    },
  },
  vAlign: {
    verticalAlign: "top",
  },
}));

export default function SendInBoxStep1() {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [beeCareOrder, setBeeCareOrder] = useContext(BeeCareOrderContext);
  const [user, setUser, getUser] = useContext(UserContext);
  const settings = useContext(SettingsContext).beecare;
  const { getTypeImage, getTypeName } = useFormatter();
  const [modalOpenStatus, setModalOpenStatus] = useState(false);

  const [selected, setSelected] = useState(beeCareOrder.selectedHouses);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [addOnProducts, setAddOnProducts] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(true);
  const isSelected = (code) => selected.indexOf(code) !== -1;
  const [price, setPrice] = useState(references.defaultOrders.beecare.price);

  const getHouseOptions = (code) => {
    const house = user.houses.find((h) => h.code === code);
    return (
      selectedOptions[code] || {
        houseCode: code,
        houseId: house?.id || null,
        addOns: [],
      }
    );
  };

  const notMobile = useMediaQuery(useTheme().breakpoints.up("sm"));

  const openNewBeeHome = () => {
    setModalOpenStatus(true);
  };

  const closeModal = () => {
    setModalOpenStatus(false);
    getUser();
  };

  const handleClick = (event, house) => {
    if (!house.inSeason.isSentIn) {
      const code = house.code;
      const selectedIndex = selected.indexOf(code);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, code);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
        setSelectedOptions((prev) => {
          const { [code]: _, ...rest } = prev;
          return rest;
        });
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
        setSelectedOptions((prev) => {
          const { [code]: _, ...rest } = prev;
          return rest;
        });
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
        setSelectedOptions((prev) => {
          const { [code]: _, ...rest } = prev;
          return rest;
        });
      }

      setSelected(newSelected);
    }
  };

  // Initialize selectedOptions from beeCareOrder.additionalOptions only on mount
  useEffect(() => {
    if (
      beeCareOrder.additionalOptions &&
      beeCareOrder.additionalOptions.length > 0
    ) {
      const newSelectedOptions = {};
      beeCareOrder.additionalOptions.forEach((option) => {
        if (option.houseCode && option.addOnDetails) {
          const house = user.houses.find((h) => h.code === option.houseCode);
          newSelectedOptions[option.houseCode] = {
            houseCode: option.houseCode,
            houseId: option.houseId || house?.id || null,
            addOns: option.addOnDetails.map((detail) => detail.id) || [],
          };
        }
      });
      setSelectedOptions(newSelectedOptions);
    }
  }, [user.houses]); // Include user.houses in dependencies

  useEffect(() => {
    if (settings.shopifyPrice) {
      setPrice(settings.shopifyPrice);
    }
  }, [settings.shopifyPrice]);

  useEffect(() => {
    if (
      user.houses.length === 0 &&
      history.location.state &&
      history.location.state.data.length > 0
    ) {
      setUser((previousUser) => ({
        ...previousUser,
        houses: history.location.state.data,
      }));
    }
  }, [setUser, user.houses.length]);

  useEffect(() => {
    setBeeCareOrder((prevBeeCareOrder) => ({
      ...prevBeeCareOrder,
      selectedHouses: selected,
      amount: selected.length,
      discount_vouchers: [],
    }));
  }, [selected, setBeeCareOrder]);

  useEffect(() => {
    setBeeCareOrder((prevBeeCareOrder) => ({
      ...prevBeeCareOrder,
      houses: selected.map((sel) => {
        return user.houses.find((house) => {
          return house.code.toUpperCase() === sel.toUpperCase();
        }).id;
      }),
    }));
  }, [selected, user.houses, setBeeCareOrder]);

  useEffect(() => {
    const fetchAddOns = async () => {
      try {
        const products = await fetchBeeHomeAddOnProducts(i18n.language); // Or get language from context/settings
        setAddOnProducts(products);
      } catch (error) {
        console.error("Failed to fetch add-on products:", error);
      } finally {
        setLoadingAddOns(false);
      }
    };

    fetchAddOns();
  }, []);

  useEffect(() => {
    // Convert selectedOptions to the format expected by calculateCompleteOrderTotal
    const optionsArray = Object.values(selectedOptions).map((option) => {
      const addOnDetails = [];
      if (option.addOns && option.addOns.length > 0) {
        option.addOns.forEach((addOnId) => {
          const product = addOnProducts.find((p) => p.id === addOnId);
          if (product) {
            addOnDetails.push(product);
          }
        });
      }
      return { ...option, addOnDetails };
    });

    // Use the centralized calculation function that handles bulk pricing
    const orderTotals = calculateCompleteOrderTotal({
      selectedHouses: selected,
      allShopifyVariants: settings?.shopify?.products?.variants || [],
      discount_vouchers: beeCareOrder.discount_vouchers,
      donation: beeCareOrder.donation,
      additionalOptions: optionsArray,
      additionalProducts: beeCareOrder.additionalProducts || [],
    });

    setBeeCareOrder((prevBeeCareOrder) => ({
      ...prevBeeCareOrder,
      total_price: orderTotals.finalTotal,
      variant: orderTotals.variant,
      price: orderTotals.originalPrice,
      priceReduction: orderTotals.priceReduction,
      additionalOptions: optionsArray,
    }));
  }, [
    selected,
    settings?.shopify?.products?.variants,
    beeCareOrder.discount_vouchers,
    beeCareOrder.donation,
    beeCareOrder.additionalProducts,
    selectedOptions,
    addOnProducts,
  ]);

  const nextPage = (e) => {
    e.preventDefault();

    if (selected.length) {
      history.push("/sendinbox/return-address");
    }
  };

  useMount(getUser);

  const updateBeecareOptions = () => {
    setBeeCareOrder((prevBeeCareOrder) => ({
      ...prevBeeCareOrder,
      additionalOptions: selectedOptions,
    }));
  };

  const renderTableBody = () => {
    if (notMobile) {
      // Desktop
      return (
        <TableBody>
          {user.houses &&
            user.houses.length > 0 &&
            user.houses[0].inSeason &&
            user.houses.map((value) => {
              const labelId = `checkbox-list-label-${value.id}`;
              const isItemSelected = isSelected(value.code);
              const isSentIn = value.inSeason.isSentIn;
              const tracking_number = value.inSeason.tracking_number;
              const tracking_url = value.inSeason.tracking_url;
              const shipping_label = value.inSeason.shipping_label;
              return (
                <React.Fragment key={value.code}>
                  <TableRow
                    hover={!isSentIn}
                    onClick={(event) => handleClick(event, value)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{ "aria-labelledby": labelId }}
                        disabled={isSentIn}
                        className={isItemSelected ? classes.checkboxActive : ""}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      className={
                        isSentIn
                          ? classes.houseRowInActive
                          : classes.houseRowActive
                      }
                    >
                      <img
                        src={getTypeImage(value.type)}
                        alt={"image of " + value.type}
                        className={`${classes.houseImage} ${
                          isItemSelected ? classes.imgSelected : ""
                        }`}
                      />
                    </TableCell>
                    <TableCell
                      className={
                        isSentIn
                          ? classes.houseRowInActive
                          : classes.houseRowActive
                      }
                    >
                      <List component="div">
                        <Tooltip
                          title={t("House code")}
                          placement="bottom"
                          arrow
                        >
                          <ListItemText
                            className={`${classes.listitem} ${classes.listTitle}`}
                            primary={value.name ? value.name : value.code}
                            secondary={
                              value.name ? value.code : getTypeName(value.type)
                            }
                          />
                        </Tooltip>
                      </List>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t("298")} placement="bottom" arrow>
                        {/* Requires to be wrapped in div else tooltip throws child error */}
                        <div>
                          {tracking_number && (
                            <Link href={tracking_url} target="_blank">
                              {tracking_number}
                            </Link>
                          )}
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {shipping_label && (
                        <Link href={shipping_label} target="_blank">
                          {t("301")}
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                  {!loadingAddOns && addOnProducts.length > 0 && (
                    <TableRow>
                      <TableCell style={{ padding: 0 }} colSpan={5}>
                        <Collapse in={isItemSelected}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            sx={{ py: 2 }}
                          >
                            <Box display="flex">
                              <Typography variant="h6" sx={{ mr: 1 }}>
                                {t("521", { houseCode: value.code ?? "" })}
                              </Typography>
                              <Tooltip
                                title={t("more_information")}
                                placement="top"
                                arrow
                              >
                                <Link
                                  href={t("522")}
                                  target="_blank"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <InfoOutlined
                                    fontSize="small"
                                    color="primary"
                                  />
                                </Link>
                              </Tooltip>
                            </Box>
                          </Box>
                          <List disablePadding>
                            {addOnProducts.map((product) => {
                              const isSelected = getHouseOptions(
                                value.code
                              ).addOns?.includes(product.id);
                              return (
                                <ListItemButton
                                  key={product.id}
                                  onClick={() =>
                                    setSelectedOptions((prev) => {
                                      const currentOptions = getHouseOptions(
                                        value.code
                                      );
                                      const newAddOns = isSelected
                                        ? currentOptions.addOns.filter(
                                            (id) => id !== product.id
                                          )
                                        : [
                                            ...(currentOptions.addOns || []),
                                            product.id,
                                          ];
                                      return {
                                        ...prev,
                                        [value.code]: {
                                          ...currentOptions,
                                          houseId: value.id,
                                          addOns: newAddOns,
                                        },
                                      };
                                    })
                                  }
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    inputProps={{
                                      "aria-label": `${product.productTitle} checkbox`,
                                    }}
                                  />
                                  <ListItemText
                                    primary={`${product.productTitle}`}
                                    secondary={`${Number(product.price).toFixed(
                                      2
                                    )} ${product.currency}`}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
        </TableBody>
      );
    } else {
      // Mobile
      return (
        <TableBody>
          {user.houses &&
            user.houses.map((value) => {
              const labelId = `checkbox-list-label-${value.id}`;
              const isItemSelected = isSelected(value.code);
              let isSentIn = null;
              let tracking_number = null;
              let tracking_url = null;
              let shipping_label = null;
              if (value.inSeason) {
                isSentIn = value.inSeason.isSentIn;
                tracking_number = value.inSeason.tracking_number;
                tracking_url = value.inSeason.tracking_url;
                shipping_label = value.inSeason.shipping_label;
              }
              return (
                <React.Fragment key={value.code}>
                  <TableRow
                    hover={!isSentIn}
                    onClick={(event) => handleClick(event, value)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{ "aria-labelledby": labelId }}
                        disabled={isSentIn}
                        className={isItemSelected ? classes.checkboxActive : ""}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <img
                        src={getTypeImage(value.type)}
                        alt={"image of " + value.type}
                        className={`${classes.houseImage} ${
                          isItemSelected ? classes.imgSelected : ""
                        } ${
                          isSentIn
                            ? classes.houseRowInActive
                            : classes.houseRowActive
                        }`}
                      />
                    </TableCell>
                    <TableCell>
                      <List
                        component="div"
                        disablePadding={true}
                        className={
                          isSentIn
                            ? classes.houseRowInActive
                            : classes.houseRowActive
                        }
                      >
                        <Tooltip
                          title={t("House code")}
                          placement="bottom"
                          arrow
                        >
                          <ListItemText
                            className={`${classes.listitem} ${classes.listTitle}`}
                            primary={value.name ? value.name : value.code}
                            secondary={
                              value.name ? value.code : getTypeName(value.type)
                            }
                          />
                        </Tooltip>
                      </List>
                      <Tooltip title={t("298")} placement="bottom" arrow>
                        {/* Requires to be wrapped in div else tooltip throws child error */}
                        <div>
                          {tracking_number && (
                            <Link href={tracking_url} target="_blank">
                              {tracking_number}
                            </Link>
                          )}
                        </div>
                      </Tooltip>
                      {shipping_label && (
                        <Link href={shipping_label} target="_blank">
                          {t("301")}
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={3}>
                      <Collapse in={isItemSelected}>
                        <Box
                          display="flex"
                          alignItems="center"
                          sx={{ px: 2, py: 1 }}
                        >
                          <Typography variant="h6" sx={{ mr: 1 }}>
                            {t("521", { houseCode: value.code ?? "" })}
                          </Typography>
                          <Tooltip
                            title={t("more_information")}
                            placement="top"
                            arrow
                          >
                            <Link
                              href={t("522")}
                              target="_blank"
                              sx={{
                                ml: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <InfoOutlined fontSize="small" color="primary" />
                            </Link>
                          </Tooltip>
                        </Box>
                        <List disablePadding>
                          {!loadingAddOns && addOnProducts.length > 0 && (
                            <List disablePadding>
                              {addOnProducts.map((product) => {
                                const isSelected = getHouseOptions(
                                  value.code
                                ).addOns?.includes(product.id);
                                return (
                                  <ListItemButton
                                    key={product.id}
                                    onClick={() =>
                                      setSelectedOptions((prev) => {
                                        const currentOptions = getHouseOptions(
                                          value.code
                                        );
                                        const newAddOns = isSelected
                                          ? currentOptions.addOns.filter(
                                              (id) => id !== product.id
                                            )
                                          : [
                                              ...(currentOptions.addOns || []),
                                              product.id,
                                            ];
                                        return {
                                          ...prev,
                                          [value.code]: {
                                            ...currentOptions,
                                            houseId: value.id,
                                            addOns: newAddOns,
                                          },
                                        };
                                      })
                                    }
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      inputProps={{
                                        "aria-label": `${product.productTitle} checkbox`,
                                      }}
                                    />
                                    <ListItemText
                                      primary={`${product.productTitle}`}
                                      secondary={`${Number(
                                        product.price
                                      ).toFixed(2)} ${product.currency}`}
                                    />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          )}
                          {loadingAddOns && (
                            <Typography>{t("loading_addons")}</Typography>
                          )}
                          {!loadingAddOns && addOnProducts.length === 0 && (
                            <Typography>{t("no_addons_available")}</Typography>
                          )}
                        </List>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
        </TableBody>
      );
    }
  };

  return (
    <BeeCareOrderSideBar step={0} showSummary>
      <BeehomeModal house={{}} open={modalOpenStatus} onClose={closeModal} />
      <WBPPage
        title={t("Step 1 Select boxes")}
        description={
          <Trans
            i18nKey={"296"}
            components={{
              header: <strong />,
            }}
          />
        }
      >
        <Grid container item justifyContent="flex-end">
          <Box mb={notMobile ? 0 : 2}>
            <Button
              id="button-sendinboxstep1-addabeehome"
              variant="outlined"
              color="primary"
              onClick={openNewBeeHome}
              className={classes.rightMargin}
              startIcon={<StoreOutlined />}
              disableElevation
            >
              {t("Add a BeeHome")}
            </Button>
          </Box>
        </Grid>
        <form onSubmit={nextPage}>
          <Box mb={4}>
            <TableContainer>
              <Table
                aria-label="houses table"
                padding={notMobile ? "normal" : "none"}
              >
                <TableHead>
                  {notMobile ? (
                    // notMobile
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ) : (
                    // !notMobile
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableHead>
                {renderTableBody()}
              </Table>
            </TableContainer>
          </Box>
          <Box component="div" mb={4}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Button
                  id="button-sendinboxstep1-back"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    history.goBack();
                  }}
                  disableElevation
                >
                  {t("404")}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  id="button-sendinboxstep1-submit"
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!selected.length}
                  disableElevation
                >
                  {t("406")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      </WBPPage>
    </BeeCareOrderSideBar>
  );
}
